import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { makeStyles, tokens, Tag, TagGroup } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";

/** Three rows of tags: 3 * 24px + 2 * 4px of row gap. Beyond that, scroll. */
const TAG_ROWS_MAX_HEIGHT = "80px";

const useStyles = makeStyles({
  box: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "4px",
    minHeight: "32px",
    padding: "3px 6px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    ":focus-within": { border: `1px solid ${tokens.colorBrandStroke1}` },
  },
  tagArea: {
    maxHeight: TAG_ROWS_MAX_HEIGHT,
    overflowY: "auto",
  },
  // TagGroup's own root is inline-flex with only a columnGap, so tags never wrap
  // and overflow the field instead. These three declarations are what make a
  // long recipient list fall onto additional rows.
  tagGroup: {
    display: "flex",
    flexWrap: "wrap",
    rowGap: "4px",
  },
  input: {
    minWidth: 0,
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    padding: "2px",
  },
});

interface RecipientsInputProps {
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const RecipientsInput: React.FC<RecipientsInputProps> = ({ value, onChange, placeholder }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [current, setCurrent] = useState("");
  const tagAreaRef = useRef<HTMLDivElement>(null);
  const prevCount = useRef(value.length);

  // Keep the newest recipients visible once the tag area starts scrolling.
  useEffect(() => {
    if (value.length > prevCount.current && tagAreaRef.current) {
      tagAreaRef.current.scrollTop = tagAreaRef.current.scrollHeight;
    }
    prevCount.current = value.length;
  }, [value.length]);

  const commit = () => {
    const parts = current
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length) {
      const next = [...value];
      for (const p of parts) if (!next.includes(p)) next.push(p);
      onChange(next);
    }
    setCurrent("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && current === "" && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className={styles.box}>
      {value.length > 0 && (
        <div className={styles.tagArea} ref={tagAreaRef}>
          <TagGroup
            className={styles.tagGroup}
            onDismiss={(_, data) => onChange(value.filter((v) => v !== data.value))}
          >
            {value.map((v) => (
              <Tag
                key={v}
                value={v}
                size="small"
                dismissible
                dismissIcon={{ "aria-label": t("recipients.remove") }}
              >
                {v}
              </Tag>
            ))}
          </TagGroup>
        </div>
      )}
      <input
        className={styles.input}
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : ""}
        type="tel"
        aria-label={t("recipients.label")}
      />
    </div>
  );
};

export default RecipientsInput;
